import React, { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import {
  dashboard2SectionDescriptionSx,
  dashboard2SectionIntroSx,
  dashboard2PageIntroSx,
  dashboard2PageIntroTitleSx,
  dashboard2SectionIntroTitleSx
} from "../dashboard2-status.styles";

type Density = "page" | "section" | "compact";

type Dashboard2SectionIntroProps = {
  title: string;
  description?: string;
  titleComponent?: "h1" | "h2" | "h3";
  density?: Density;
  /** Optional control. Stacks under the text when this block is narrow; sits on the right when it is at least 480px wide. */
  action?: ReactNode;
  "data-testid"?: string;
};

const densitySx = {
  page: dashboard2PageIntroSx,
  section: dashboard2SectionIntroSx,
  compact: dashboard2SectionIntroSx
} as const;

const titleSx = {
  h1: dashboard2PageIntroTitleSx,
  h2: dashboard2SectionIntroTitleSx,
  h3: dashboard2SectionIntroTitleSx
} as const;

/** Row layout once this block is at least 480px wide; stacked below that. */
const introRowSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 1,
  width: "100%",
  "@container (min-width: 480px)": {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 2
  }
} as const;

const introTextGroupSx = {
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
  minWidth: 0,
  maxWidth: "65ch",
  flex: "1 1 auto"
} as const;

export const Dashboard2SectionIntro: React.FC<Dashboard2SectionIntroProps> = ({
  title,
  description,
  titleComponent = "h2",
  density = "section",
  action,
  "data-testid": testId
}) => {
  return (
    <Box sx={{ ...densitySx[density], containerType: "inline-size" }} data-testid={testId}>
      <Box sx={introRowSx}>
        <Box sx={introTextGroupSx}>
          <Typography
            component={titleComponent}
            variant="inherit"
            sx={{ ...titleSx[titleComponent], minWidth: 0 }}
          >
            {title}
          </Typography>
          {description ? (
            <Typography
              variant="inherit"
              color="text.secondary"
              sx={dashboard2SectionDescriptionSx}
            >
              {description}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Box>
    </Box>
  );
};
